import { Vec2 } from './Vec2'
import { Rectangle } from './Rectangle'

class FadeElement {

	private boundingBox: Rectangle

	private  element: HTMLElement
	
	private  fadeSpeed: number

	private fadeInDelayS: number
	
	private fadeOutDelayS: number

	private  translate : Vec2

	private  bVisible: boolean
	
	private fadeTimeout: NodeJS.Timeout | null
	
	private blurSize : number

	private  bFadeInOnly : boolean

	private debugText: string | null
	
	private defaultCssTransition : string

	constructor( htmlElement: HTMLElement ) {
		this.element = htmlElement

		this.fadeSpeed = 0.5
		this.fadeInDelayS = 0.1
		this.fadeOutDelayS = 5.0

		this.bVisible = false
		this.fadeTimeout = null
		this.bFadeInOnly = false
		this.translate = new Vec2( 0.0, 0.0 )
		this.blurSize = 0.0
		this.defaultCssTransition = ''

		this.boundingBox = new Rectangle( new Vec2( 0.0, 0.0 ), new Vec2( 0.0, 0.0 ) )

		this.debugText = null

		this.initElement()
		this.updatePositions()
	}

	private initElement(): void {

		this.setSpeed( parseFloat( this.element.getAttribute( 'data-fade-speed' ) || '1.0' ) );
		this.setDelay( parseFloat( this.element.getAttribute( 'data-fade-delay' ) || '0.1' ) );
		this.setTranslateX( parseFloat( this.element.getAttribute( 'data-fade-translatex' ) || '0.0' ) );
		this.setTranslateY( parseFloat( this.element.getAttribute( 'data-fade-translatey' ) || '0.0' ) );
		this.setBlurSize( parseFloat( this.element.getAttribute( 'data-fade-blursize' ) || '0.0' ) );
		this.setFadeInOnly( ( this.element.getAttribute( 'data-fade-inonly' ) || 'false' ) === 'true' );
		this.debugText = this.element.getAttribute( 'data-fade-data' ) || null;
		this.defaultCssTransition = this.element.style.transition || '';

		// The fade-in/scroll-reveal effect is disabled site-wide: elements are
		// shown immediately instead of starting hidden and animating in.
		this.element.style.opacity = '1.0';
		this.element.style.visibility = 'visible';
	}

	private setSpeed( s: number ): void {
		this.fadeSpeed = s
	}

	private setDelay( d: number ): void {
		this.fadeInDelayS = d
	}

	private setFadeInOnly( b: boolean ): void {
		this.bFadeInOnly = b
	}

	public setTranslateX( tx: number ): void {
		this.translate.x = tx
	}

	public setTranslateY( ty: number ): void {
		this.translate.y = ty
	}

	public setBlurSize( bs: number ): void {
		this.blurSize = bs
	}

	public updatePositions(): void {
		let scrollX: number = window.scrollX
		let scrollY: number = window.scrollY
		let rect: DOMRect = this.element.getBoundingClientRect()
		this.boundingBox = new Rectangle(
			new Vec2( rect.left + scrollX - this.translate.x, rect.top + scrollY - this.translate.y ),
			new Vec2( rect.width, rect.height )
		)
	}

	public setVisible( v: boolean ): void {
		// The fade in/out effect is disabled site-wide: elements are already
		// shown by initElement() and stay that way regardless of scroll
		// position.
	}


	isVisible(): boolean {
		return this.bVisible
	}



	isInsideWindow( windowBoundingBox: Rectangle ): boolean {
		return this.boundingBox.intersects( windowBoundingBox );
	}


	fadeIn(): void {
		this.element.style.visibility = 'visible';

		let completeCssTransition: string = this.defaultCssTransition;
		if (completeCssTransition.length > 0) {
			completeCssTransition += ', ';
		}
		completeCssTransition += `opacity ${ this.fadeSpeed }s ease-in-out`;

		if ( this.translate.x !== 0.0 ) {
			completeCssTransition += `, left ${ this.fadeSpeed }s ease-in-out`;
			this.element.style.left = '0px';
		}
		if ( this.translate.y !== 0.0 ) {
			completeCssTransition += `, top ${ this.fadeSpeed }s ease-in-out`;
			this.element.style.top = '0px';
		}
		if ( this.blurSize > 0.0 ) {
			completeCssTransition += `, filter ${ this.fadeSpeed }s ease-in-out`;
			this.element.style.filter = 'blur( 0px )';
		}

		this.element.style.transition = completeCssTransition;
		this.element.style.opacity = '1.0';
	}

	fadeOut(): void {
		this.element.style.visibility = 'hidden';
		this.element.style.transition = '';
		this.element.style.opacity = '0.0';
		if ( this.translate.x !== 0.0 ) {
			this.element.style.left = `${ this.translate.x }px`;
		}
		if ( this.translate.y !== 0.0 ) {
			this.element.style.top = `${ this.translate.y }px`;
		}
		if ( this.blurSize > 0.0 ) {
			this.element.style.filter = `blur( ${ this.blurSize }px )`;
		}
	}



	getElement(): HTMLElement {
		return this.element
	}

	getDebugText(): string | null {
		return this.debugText
	}

	getBoundingBox(): Rectangle {
		return this.boundingBox
	}

	
}




class FadeEngine {


	private fadeElements: FadeElement[]

	private recomputeTimeout: NodeJS.Timeout | null

	constructor() {
		this.fadeElements = []
		this.recomputeTimeout = null
	}

	useEffect(): void {
		this.initEvents()
		this.initElements()
		this.updateVisibilityWithCurrentWindow()
	}

	onPageLoaded(): void {
		this.recomputeElementPositions()
		this.updateVisibilityWithCurrentWindow()
	}

	private initElements(): void {
		this.fadeElements = []
		document.querySelectorAll<HTMLElement>( '[data-fade]' ).forEach( ( element: Element ): void => {
			if ( element instanceof HTMLElement ) {
				 this.fadeElements.push( new FadeElement( element ) )
			 }
		} )

		document.querySelectorAll<HTMLElement>( '[data-scrollable]' ).forEach( ( element: Element ): void => {
			if ( element instanceof HTMLElement ) {
				element.removeEventListener('scroll', this)
				element.addEventListener('scroll', this)
			}
		} )

	}
	

	private initEvents(): void {
		window.removeEventListener( 'load', this )
		window.addEventListener( 'load', this )

		window.removeEventListener( 'resize', this )
		window.addEventListener( 'resize', this )

		window.removeEventListener( 'scroll', this )
		window.addEventListener( 'scroll', this )
	}


	private onResize(): void {
		this.launchRecomputeTimeout()
	}

	private launchRecomputeTimeout(): void {
		if ( this.recomputeTimeout !== null ) {
			return
		}

		this.recomputeTimeout = setTimeout( (): void => {
			this.recomputeElementPositions()
			this.recomputeTimeout = null
		}, 100 )
	}

	private updateVisibilityWithCurrentWindow(): void {
		// Shrink the trigger zone so elements reveal once meaningfully in view,
		// rather than the instant a single pixel touches the viewport edge.
		let revealMarginRatio: number = 0.15
		let revealMargin: number = window.innerHeight * revealMarginRatio

		let windowP: Vec2 = new Vec2( window.scrollX, window.scrollY + revealMargin )
		let windowS: Vec2 = new Vec2( window.innerWidth, Math.max( window.innerHeight - revealMargin * 2.0, 0.0 ) )

		let windowBoundingBox: Rectangle = new Rectangle( windowP, windowS )

		this.fadeElements.forEach( ( fe: FadeElement ): void => {
			if ( fe.isInsideWindow( windowBoundingBox ) ) {
				fe.setVisible( true )
			} else {
				fe.setVisible( false )
			}
		} )
	}

	private onScroll(): void {

		this.updateVisibilityWithCurrentWindow()
		this.launchRecomputeTimeout()

	}

	private recomputeElementPositions(): void {
		this.fadeElements.forEach( ( fe: FadeElement ): void => {
			fe.updatePositions()
		} )

		this.updateVisibilityWithCurrentWindow()
	}

	handleEvent( e: Event ): void {
		switch ( e.type ) {
			case 'load':
				this.onPageLoaded()
				break
			case 'resize':
				this.onResize()
				break
			case 'scroll':
				this.onScroll()
				break
		}
	}


} 



export let fadeEngine: FadeEngine = new FadeEngine()